<script setup lang="ts">
import { countries } from '~/assets/data/countries';
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { toTypedSchema } from "@vee-validate/zod";
import * as zod from 'zod';
import Logo from '@images/logos/eaccme.svg';
import { CourseCertificateStatus } from "~/enums/course.enums";

const { dispatchMessage } = useSystemMessages();
const { submitClaimCertificateForm, startDownload } = useCertificate();
const { course } = useCourseState();
const { user } = useUserStore();
const { t } = useI18n();

const loading = ref(false);

const emit = defineEmits<{
  (e: 'downloaded'): void;
}>();

// todo: refactor
const validationOptions = {
  validateOnBlur: true,
  validateOnModelUpdate: false,
};

/*
 * TODO: is used in several places, needs to be checked in terms of json localization structure
 */

/**
 *  1. Quality of the ELM
 */
const UsefulnessOptions = [
  t('course.claimCertificate.form.relevanceOfMaterial.fields.usefulnessPersonal.options.undecided'),
  t('course.claimCertificate.form.relevanceOfMaterial.fields.usefulnessPersonal.options.notUseful'),
  t('course.claimCertificate.form.relevanceOfMaterial.fields.usefulnessPersonal.options.fairlyUseful'),
  t('course.claimCertificate.form.relevanceOfMaterial.fields.usefulnessPersonal.options.useful'),
  t('course.claimCertificate.form.relevanceOfMaterial.fields.usefulnessPersonal.options.extremelyUseful'),
];

const UsefulnessOptionsValuesMapping = {
  [UsefulnessOptions[0]]: 'undecided',
  [UsefulnessOptions[1]]: 'not_useful',
  [UsefulnessOptions[2]]: 'fairly_useful',
  [UsefulnessOptions[3]]: 'useful',
  [UsefulnessOptions[4]]: 'extremely_useful',
};

const OverallImpressionOptions = [
  t('course.claimCertificate.form.qualityOfELM.fields.overallImpressionOrganization.options.veryPoor'),
  t('course.claimCertificate.form.qualityOfELM.fields.overallImpressionOrganization.options.poor'),
  t('course.claimCertificate.form.qualityOfELM.fields.overallImpressionOrganization.options.fairlyGood'),
  t('course.claimCertificate.form.qualityOfELM.fields.overallImpressionOrganization.options.good'),
  t('course.claimCertificate.form.qualityOfELM.fields.overallImpressionOrganization.options.excellent'),
];

const OverallImpressionOptionsValuesMapping = {
  [OverallImpressionOptions[0]]: 'very_poor',
  [OverallImpressionOptions[1]]: 'poor',
  [OverallImpressionOptions[2]]: 'fairly_good',
  [OverallImpressionOptions[3]]: 'good',
  [OverallImpressionOptions[4]]: 'excellent',
};

/**
 *  2. Relevance of Material
 */
// todo: refactor
const EducationalGoalsAndScientificEvidenceOptions = [
  t('course.claimCertificate.form.relevanceOfMaterial.fields.achievementOfEducationalGoals.options.undecided'),
  t('course.claimCertificate.form.relevanceOfMaterial.fields.achievementOfEducationalGoals.options.notAtAll'),
  t('course.claimCertificate.form.relevanceOfMaterial.fields.achievementOfEducationalGoals.options.notMuch'),
  t('course.claimCertificate.form.relevanceOfMaterial.fields.achievementOfEducationalGoals.options.somewhat'),
  t('course.claimCertificate.form.relevanceOfMaterial.fields.achievementOfEducationalGoals.options.veryMuch'),
];

const EducationalGoalsAndScientificEvidenceOptionsValuesMapping = {
  [EducationalGoalsAndScientificEvidenceOptions[0]]: 'undecided',
  [EducationalGoalsAndScientificEvidenceOptions[1]]: 'not_at_all',
  [EducationalGoalsAndScientificEvidenceOptions[2]]: 'not_much',
  [EducationalGoalsAndScientificEvidenceOptions[3]]: 'somewhat',
  [EducationalGoalsAndScientificEvidenceOptions[4]]: 'very_much',
};

/**
 * 3. Suitability of formats used during the material
 */
const LearnerEngagementTimeOptions = [
  t('course.claimCertificate.form.suitability.fields.learnerEngagementTime.options.undecided'),
  t('course.claimCertificate.form.suitability.fields.learnerEngagementTime.options.never'),
  t('course.claimCertificate.form.suitability.fields.learnerEngagementTime.options.onlyRarely'),
  t('course.claimCertificate.form.suitability.fields.learnerEngagementTime.options.yesSometimes'),
  t('course.claimCertificate.form.suitability.fields.learnerEngagementTime.options.yesAlwaysOrAlmostAlways'),
];

const LearnerEngagementTimeOptionsValuesMapping = {
  [LearnerEngagementTimeOptions[0]]: 'undecided',
  [LearnerEngagementTimeOptions[1]]: 'never',
  [LearnerEngagementTimeOptions[2]]: 'only_rarely',
  [LearnerEngagementTimeOptions[3]]: 'yes_sometimes',
  [LearnerEngagementTimeOptions[4]]: 'yes_always_or_almost_always',
};

/**
 *  4 Ways the material affects clinical practice
 */
const InformationImplementationOptions = [
  t('course.claimCertificate.form.waysMaterialAffectsClinicalPractice.fields.informationImplementation.options.undecided'),
  t('course.claimCertificate.form.waysMaterialAffectsClinicalPractice.fields.informationImplementation.options.notAtAll'),
  t('course.claimCertificate.form.waysMaterialAffectsClinicalPractice.fields.informationImplementation.options.notMuch'),
  t('course.claimCertificate.form.waysMaterialAffectsClinicalPractice.fields.informationImplementation.options.somewhat'),
  t('course.claimCertificate.form.waysMaterialAffectsClinicalPractice.fields.informationImplementation.options.veryMuch'),

];

const InformationImplementationOptionsValuesMapping = {
  [InformationImplementationOptions[0]]: 'undecided',
  [InformationImplementationOptions[1]]: 'not_at_all',
  [InformationImplementationOptions[2]]: 'not_much',
  [InformationImplementationOptions[3]]: 'somewhat',
  [InformationImplementationOptions[4]]: 'very_much',
};

const FacultyConflictDeclarationOptions = [
  t('course.claimCertificate.form.commercialBias.fields.facultyConflictDeclaration.options.undecided'),
  t('course.claimCertificate.form.commercialBias.fields.facultyConflictDeclaration.options.no'),
  t('course.claimCertificate.form.commercialBias.fields.facultyConflictDeclaration.options.yesOnlySmallPart'),
  t('course.claimCertificate.form.commercialBias.fields.facultyConflictDeclaration.options.yesMajority'),
  t('course.claimCertificate.form.commercialBias.fields.facultyConflictDeclaration.options.yesAll'),
];

const FacultyConflictDeclarationOptionsValuesMapping = {
  [FacultyConflictDeclarationOptions[0]]: 'undecided',
  [FacultyConflictDeclarationOptions[1]]: 'no',
  [FacultyConflictDeclarationOptions[2]]: 'yes_but_only_small_part',
  [FacultyConflictDeclarationOptions[3]]: 'yes_for_majority',
  [FacultyConflictDeclarationOptions[4]]: 'yes_all',
};

const InformationFreeOfBiasOptions = [
  t('course.claimCertificate.form.commercialBias.fields.informationFreeOfBias.options.undecided'),
  t('course.claimCertificate.form.commercialBias.fields.informationFreeOfBias.options.stronglyDisagree'),
  t('course.claimCertificate.form.commercialBias.fields.informationFreeOfBias.options.ratherDisagree'),
  t('course.claimCertificate.form.commercialBias.fields.informationFreeOfBias.options.ratherAgree'),
  t('course.claimCertificate.form.commercialBias.fields.informationFreeOfBias.options.stronglyAgree'),
];

const InformationFreeOfBiasOptionsValuesMapping = {
  [InformationFreeOfBiasOptions[0]]: 'undecided',
  [InformationFreeOfBiasOptions[1]]: 'strongly_disagree',
  [InformationFreeOfBiasOptions[2]]: 'rather_disagree',
  [InformationFreeOfBiasOptions[3]]: 'rather_agree',
  [InformationFreeOfBiasOptions[4]]: 'strongly_agree',
};
/*
 * schema
 */
const schema = toTypedSchema(
  zod.object({

    /**
     * PERSONAL INFORMATION
     */
    firstName: zod.string().min(1, t('course.claimCertificate.form.errors.fieldIsRequired')),
    lastName: zod.string().min(1, t('course.claimCertificate.form.errors.fieldIsRequired')),

    userIsDoctorOfMedicine: zod.boolean({ message: t('course.claimCertificate.form.errors.fieldIsRequired') }),

    country: zod.object({
      isoCode: zod.string({ message: t('course.claimCertificate.form.errors.fieldIsRequired') })
        .refine(countryIsoCode => countries.map(country => country.code).includes(countryIsoCode), {
          message: t('course.claimCertificate.form.errors.fieldIsRequired'),
        }),
      stateName: zod.string().optional(),
    }).refine(country => {
      if (country.isoCode === 'US' && (!country.stateName || country.stateName.trim() === '')) {
        return false;
      }
      return true;
    }, {
      message: t('course.claimCertificate.form.personalInformation.fields.state.validation.requiredForUS'),
      path: ['stateName'],
    }),

    /**
     * QUALITY OF ELM
     */
    usefulnessOfMaterial: zod.object({
      // has to be in UsefulnessOptions
      usefulnessProfessional: zod.string({ message: t('course.claimCertificate.form.errors.fieldIsRequired') })
        .refine(
          val => Object.values(UsefulnessOptions).includes(val),
          { message: t('course.claimCertificate.form.errors.fieldIsRequired') },
        ),
      notUsefulExplanationProfessional: zod.string().optional(),

      usefulnessPersonal: zod.string({ message: t('course.claimCertificate.form.errors.fieldIsRequired') })
        .refine(
          val => Object.values(UsefulnessOptions).includes(val),
          { message: t('course.claimCertificate.form.errors.fieldIsRequired') },
        ),
      notUsefulExplanationPersonal: zod.string().optional(),
    }).refine(data => {
      return !(
        data.usefulnessProfessional === UsefulnessOptions[1] &&
        (!data.notUsefulExplanationProfessional || data.notUsefulExplanationProfessional.trim() === '')
      );

    },
    {
      message: t('course.claimCertificate.form.relevanceOfMaterial.fields.usefulnessPersonal.validation.required'),
      path: ['notUsefulExplanationProfessional'],

    }).refine(data => {
      return !(
        data.usefulnessPersonal === UsefulnessOptions[1] &&
        (!data.notUsefulExplanationPersonal || data.notUsefulExplanationPersonal.trim() === '')
      );
    },
    {
      message: "This field is required if was not useful",
      path: ['notUsefulExplanationPersonal'],
    }),

    overallImpressionProgramme: zod.string({ message: t('course.claimCertificate.form.errors.fieldIsRequired') })
      .refine(
        val => Object.values(OverallImpressionOptions).includes(val),
        { message: t('course.claimCertificate.form.errors.fieldIsRequired') },
      ),

    overallImpressionOrganization: zod.string({ message: t('course.claimCertificate.form.errors.fieldIsRequired') })
      .refine(
        val => Object.values(OverallImpressionOptions).includes(val),
        { message: t('course.claimCertificate.form.errors.fieldIsRequired') },
      ),

    bestAspect: zod.string().min(1, t('course.claimCertificate.form.errors.fieldIsRequired')),
    worstAspect: zod.string().min(1, t('course.claimCertificate.form.errors.fieldIsRequired')),

    /**
     * RELEVANCE OF THE MATERIAL
     */
    achievementOfEducationalGoals: zod.string({ message: t('course.claimCertificate.form.errors.fieldIsRequired') })
      .refine(
        val => Object.values(EducationalGoalsAndScientificEvidenceOptions).includes(val),
        { message: t('course.claimCertificate.form.errors.fieldIsRequired') },
      ),
    basedOnScientificEvidence: zod.string({ message: t('course.claimCertificate.form.errors.fieldIsRequired') })
      .refine(
        val => Object.values(EducationalGoalsAndScientificEvidenceOptions).includes(val),
        { message: t('course.claimCertificate.form.errors.fieldIsRequired') },
      ),

    /**
     * SUITABILITY OF THE FORMATS USED DURING THE MATERIAL
     */
    learnerEngagementTime: zod.string({ message: t('course.claimCertificate.form.errors.fieldIsRequired') })
      .refine(
        val => Object.values(LearnerEngagementTimeOptions).includes(val),
        { message: t('course.claimCertificate.form.errors.fieldIsRequired') },
      ),

    innovativeElements: zod.string().min(1, t('course.claimCertificate.form.errors.fieldIsRequired')),

    /**
     * WAYS THE MATERIAL AFFECTS CLINICAL PRACTICE
     */
    informationImplementation: zod.string({ message: t('course.claimCertificate.form.errors.fieldIsRequired') })
      .refine(
        val => Object.values(InformationImplementationOptions).includes(val),
        { message: t('course.claimCertificate.form.errors.fieldIsRequired') },
      ),

    futurePracticeInfluence: zod.string().min(1, t('course.claimCertificate.form.errors.fieldIsRequired')),

    /**
     * COMMERCIAL BIAS
     */
    facultyConflictDeclaration: zod.string({ message: t('course.claimCertificate.form.errors.fieldIsRequired') })
      .refine(
        val => Object.values(FacultyConflictDeclarationOptions).includes(val),
        { message: t('course.claimCertificate.form.errors.fieldIsRequired') },
      ),

    biasedPresentationExample: zod.string().min(1, t('course.claimCertificate.form.errors.fieldIsRequired')),

    informationFreeOfBias: zod.string({ message: t('course.claimCertificate.form.errors.fieldIsRequired') })
      .refine(
        val => Object.values(InformationFreeOfBiasOptions).includes(val),
        { message: t('course.claimCertificate.form.errors.fieldIsRequired') },
      ),

    /**
     * GENERAL
     */
    userAgreedToDataSharing: zod.boolean({ message: t('course.claimCertificate.form.errors.fieldIsRequired') })
      .refine(data => data,
        { message: t('course.claimCertificate.form.errors.fieldIsRequired') },
      ),

    courseRating: zod.number({ message: t('course.claimCertificate.form.errors.fieldIsRequired') })
      .min(1, t('course.claimCertificate.form.rating.validation.min'))
      .max(5, t('course.claimCertificate.form.rating.validation.max')),
  }),
);

const { defineField, errors, handleSubmit } = useForm({
  validationSchema: schema,
  initialValues: {
    firstName: user.value?.firstName || '',
    lastName: user.value?.lastName || '',

    country: {
      isoCode: '',
    },

    /**
     * QUALITY OF ELM
     */
    usefulnessOfMaterial: {
      usefulnessProfessional: '',
      notUsefulExplanationProfessional: '',

      usefulnessPersonal: '',
      notUsefulExplanationPersonal: '',
    },
    overallImpressionProgramme: '',
    overallImpressionOrganization: '',
    bestAspect: '',
    worstAspect: '',

    /**
     * RELEVANCE OF THE MATERIAL
     */
    achievementOfEducationalGoals: '',
    basedOnScientificEvidence: '',

    /**
     * SUITABILITY OF THE FORMATS USED DURING THE MATERIAL
     */
    learnerEngagementTime: '',
    innovativeElements: '',

    /**
     * WAYS THE MATERIAL AFFECTS CLINICAL PRACTICE
     */
    informationImplementation: '',
    futurePracticeInfluence: '',

    /**
     * COMMERCIAL BIAS
     */
    facultyConflictDeclaration: '',
    biasedPresentationExample: '',
    informationFreeOfBias: '',

    /**
     * GENERAL
     */
    userAgreedToDataSharing: false,
  },
});

/**
 * PERSONAL INFORMATION
 */
const [firstName, firstNameProps] = defineField("firstName", validationOptions);
const [lastName, lastNameProps] = defineField("lastName", validationOptions);

const [userIsDoctorOfMedicine, userIsDoctorOfMedicineProps] = defineField("userIsDoctorOfMedicine", validationOptions);

const [countryIsoCode, countryIsoCodeProps] = defineField("country.isoCode", validationOptions);
const [countryStateName, countryStateNameProps] = defineField("country.stateName", validationOptions);

/**
 * QUALITY OF ELM
 */
const [usefulnessProfessional, usefulnessProfessionalProps] = defineField("usefulnessOfMaterial.usefulnessProfessional", validationOptions);
const [notUsefulExplanationProfessional, notUsefulExplanationProfessionalProps] = defineField("usefulnessOfMaterial.notUsefulExplanationProfessional", validationOptions);

const [overallImpressionProgramme, overallImpressionProgrammeProps] = defineField("overallImpressionProgramme", validationOptions);
const [overallImpressionOrganization, overallImpressionOrganizationProps] = defineField("overallImpressionOrganization", validationOptions);

const [bestAspect, bestAspectProps] = defineField("bestAspect", validationOptions);
const [worstAspect, worstAspectProps] = defineField("worstAspect", validationOptions);

/**
 * RELEVANCE OF THE MATERIAL
 */
const [achievementOfEducationalGoals, achievementOfEducationalGoalsProps] = defineField("achievementOfEducationalGoals", validationOptions);
const [basedOnScientificEvidence, basedOnScientificEvidenceProps] = defineField("basedOnScientificEvidence", validationOptions);

const [usefulnessPersonal, usefulnessPersonalProps] = defineField("usefulnessOfMaterial.usefulnessPersonal", validationOptions);
const [notUsefulExplanationPersonal, notUsefulExplanationPersonalProps] = defineField("usefulnessOfMaterial.notUsefulExplanationPersonal", validationOptions);

/**
 * SUITABILITY OF THE FORMATS USED DURING THE MATERIAL
 */
const [learnerEngagementTime, learnerEngagementTimeProps] = defineField("learnerEngagementTime", validationOptions);
const [innovativeElements, innovativeElementsProps] = defineField("innovativeElements", validationOptions);

/**
 * WAYS THE MATERIAL AFFECTS CLINICAL PRACTICE
 */
const [informationImplementation, informationImplementationProps] = defineField("informationImplementation", validationOptions);
const [futurePracticeInfluence, futurePracticeInfluenceProps] = defineField("futurePracticeInfluence", validationOptions);

/**
 * COMMERCIAL BIAS
 */
const [facultyConflictDeclaration, facultyConflictDeclarationProps] = defineField("facultyConflictDeclaration", validationOptions);
const [biasedPresentationExample, biasedPresentationExampleProps] = defineField("biasedPresentationExample", validationOptions);
const [informationFreeOfBias, informationFreeOfBiasProps] = defineField("informationFreeOfBias", validationOptions);

/**
 * GENERAL
 */
const [userAgreedToDataSharing, userAgreedToDataSharingProps] = defineField("userAgreedToDataSharing", validationOptions);
const [courseRating, courseRatingProps] = defineField("courseRating", validationOptions);

const onSubmit = handleSubmit(async values => {
  try {
    loading.value = true;

    if (!course.value?.id) {
      return;
    }

    const {
      country,
      usefulnessOfMaterial,
      overallImpressionProgramme,
      overallImpressionOrganization,

      achievementOfEducationalGoals,
      basedOnScientificEvidence,

      learnerEngagementTime,
      informationImplementation,

      facultyConflictDeclaration,
      informationFreeOfBias,

      ...restValues
    } = values;

    const body = {
      accreditationBody: 'eaccme',
      certificateType: 'cme_certificate',

      countryIsoCode: country.isoCode,
      countryStateName: country.stateName,

      usefulnessProfessional: UsefulnessOptionsValuesMapping[usefulnessOfMaterial.usefulnessProfessional],
      notUsefulExplanationProfessional: usefulnessOfMaterial.notUsefulExplanationProfessional,

      usefulnessPersonal: UsefulnessOptionsValuesMapping[usefulnessOfMaterial.usefulnessPersonal],
      notUsefulExplanationPersonal: usefulnessOfMaterial.notUsefulExplanationPersonal,

      overallImpressionProgramme: OverallImpressionOptionsValuesMapping[overallImpressionProgramme],
      overallImpressionOrganization: OverallImpressionOptionsValuesMapping[overallImpressionOrganization],

      achievementOfEducationalGoals: EducationalGoalsAndScientificEvidenceOptionsValuesMapping[achievementOfEducationalGoals],
      basedOnScientificEvidence: EducationalGoalsAndScientificEvidenceOptionsValuesMapping[basedOnScientificEvidence],

      learnerEngagementTime: LearnerEngagementTimeOptionsValuesMapping[learnerEngagementTime],
      informationImplementation: InformationImplementationOptionsValuesMapping[informationImplementation],

      facultyConflictDeclaration: FacultyConflictDeclarationOptionsValuesMapping[facultyConflictDeclaration],
      informationFreeOfBias: InformationFreeOfBiasOptionsValuesMapping[informationFreeOfBias],

      ...restValues,
    };

    const response = await submitClaimCertificateForm(course.value.id, body);

    startDownload(response.data.value as BlobPart, `${t('course.claimCertificate.form.submit.filename')} - ${course.value?.courseTitle}.pdf`);
    dispatchMessage(t('course.claimCertificate.form.submit.success.title'), 'success', t('course.claimCertificate.form.submit.success.message'));
    if (course.value){
      course.value.certificateStatus = CourseCertificateStatus.CmeClaimed;
    }
    emit('downloaded');

  } catch (error) {
    interface ClaimCertificateError {
      data: {
        message: string;
      };
    }

    dispatchMessage(t('global.error.default.title'), 'error', (error as ClaimCertificateError)?.data?.message || t('global.error.default.message'));

  } finally {
    loading.value = false;
  }
});

</script>

<template>
  <form
    class="flex flex-col gap-12"
    @submit="onSubmit"
  >
    <Card
      class="rounded-2xl p-0 text-neutral-90 shadow-medmastery"
      :pt="{
        body: {
          class: 'p-6'
        },
        content: {
          class: 'p-0'
        }
      }"
    >
      <template #content>
        <div class="flex flex-col gap-4">
          <!-- Logo and Title Section -->
          <div class="flex items-center gap-3">
            <div class="w-10 h-10 xs:w-16 xs:h-16">
              <Logo class="w-full h-full" />
            </div>
            <div class="flex-1 text-15 text-neutral-90 font-bold uppercase">
              {{t('course.claimCertificate.form.EACCME.infoCard.title')}}
            </div>
          </div>

          <!-- Address Section -->
          <div class="flex flex-col gap-2">
            <div class="text-14 text-neutral-80 font-bold uppercase">
              {{t('course.claimCertificate.form.EACCME.infoCard.address.title')}}
            </div>
            <div class="text-16 text-neutral-90 leading-130">
              <div>{{t('course.claimCertificate.form.EACCME.infoCard.address.address')}}</div>
              <div>{{t('course.claimCertificate.form.EACCME.infoCard.address.tel')}}</div>
              <div class="flex items-center gap-1">
                <a
                  href="https://eaccme.uems.eu/"
                  target="_blank"
                  class="text-blue-70 text-14 font-normal leading-150 tracking-tight underline"
                >
                  eaccme.uems.eu
                </a>
                <span class="text-neutral-80 text-14">-</span>
                <a
                  href="mailto:accreditation@uems.eu"
                  class="text-blue-70 text-14 font-normal leading-150 tracking-tight underline"
                >
                  accreditation@uems.eu
                </a>
              </div>
            </div>
          </div>

          <!-- CME Credits Section -->
          <div
            v-if="course?.cmeCredits"
            class="flex flex-col justify-center items-stretch h-10 text-18 font-bold text-neutral-90 leading-normal"
          >
            {{t('course.claimCertificate.cmeCreditsInfo', {count: course?.cmeCredits})}}
          </div>

          <!-- Course Completion Section -->
          <div
            v-sanitize-html="t('course.claimCertificate.submissionDisclaimer', {title: `<strong>${course?.courseTitle}</strong>`})"
            class="text-16 text-neutral-90 leading-150 font-normal"
          />
        </div>
      </template>
    </Card>

    <!-- PROFESSIONAL INFORMATION -->
    <div class="flex flex-col gap-4">
      <Divider
        align="left"
        type="solid"
        class="m-0 mb-2"
        :pt="{
          content: {
            class: 'bg-white'
          }
        }"
      >
        <span class="text-neutral-60 text-14 font-bold uppercase">{{t('course.claimCertificate.form.personalInformation.title')}}</span>
      </Divider>

      <div class="grid grid-cols-2 gap-x-6">
        <div class="flex flex-col">
          <FormFieldLabel for="firstName">
            {{t('course.claimCertificate.form.personalInformation.fields.firstName.title')}}
          </FormFieldLabel>

          <InputText
            id="firstName"
            v-model="firstName"
            :invalid="!!errors.firstName"
            :disabled="loading"
            aria-describedby="firstName-help"
            @blur="firstNameProps.onBlur"
          />

          <FormFieldError id="firstName-help">
            {{errors.firstName}}
          </FormFieldError>
        </div>

        <div class="flex flex-col">
          <FormFieldLabel for="lastName">
            {{t('course.claimCertificate.form.personalInformation.fields.lastName.title')}}
          </FormFieldLabel>

          <InputText
            id="lastName"
            v-model="lastName"
            :invalid="!!errors.lastName"
            :disabled="loading"
            aria-describedby="lastName-help"
            @blur="lastNameProps.onBlur"
          />

          <FormFieldError id="lastName-help">
            {{errors.lastName}}
          </FormFieldError>
        </div>
      </div>

      <div class="flex flex-col">
        <FormFieldLabel for="userIsDoctorOfMedicine">
          {{t('course.claimCertificate.form.personalInformation.fields.doctorOfMedicine.title')}}
        </FormFieldLabel>

        <Dropdown
          id="userIsDoctorOfMedicine"
          v-model="userIsDoctorOfMedicine"
          aria-describedby="userIsDoctorOfMedicine-help"
          option-label="label"
          option-value="value"
          :invalid="!!errors.userIsDoctorOfMedicine"
          :disabled="loading"
          :options="[
            {value: false, label: t('global.dropdown.options.no')},
            {value: true, label: t('global.dropdown.options.yes')}
          ]"
          @update:model-value="userIsDoctorOfMedicineProps.onBlur"
        />

        <FormFieldError id="userIsDoctorOfMedicine-help">
          {{errors.userIsDoctorOfMedicine}}
        </FormFieldError>
      </div>

      <div class="grid grid-cols-2 gap-x-6">
        <div class="flex flex-col">
          <FormFieldLabel for="countryIsoCode">
            {{t('course.claimCertificate.form.personalInformation.fields.country.title')}}
          </FormFieldLabel>

          <Dropdown
            id="countryIsoCode"
            v-model="countryIsoCode"
            option-label="name"
            option-value="code"
            editable
            aria-describedby="countryIsoCode-help"
            :invalid="!!errors['country.isoCode']"
            :disabled="loading"
            :options="countries"
            :show-clear="!!countryIsoCode"
            append-to="self"
            @update:model-value="countryIsoCodeProps.onBlur"
          >
            <template
              v-if="countryIsoCode"
              #value="{value}"
            >
              <div class="flex items-center">
                <MIcon
                  sprite-location="/icons/country_flags.svg"
                  :icon="value"
                  class="rounded-sm mr-2 country-flag-icon--value"
                />

                <div>{{countries.find((item) => item.code === value)?.name}}</div>
              </div>
            </template>

            <template #option="{option}">
              <div class="flex items-center">
                <MIcon
                  sprite-location="/icons/country_flags.svg"
                  :icon="option.code"
                  class="rounded-sm mr-2 country-flag-icon--option"
                />

                <div class="text-wrap">
                  {{option.name}}
                </div>
              </div>
            </template>
          </Dropdown>

          <FormFieldError id="countryIsoCode-help">
            {{errors['country.isoCode']}}
          </FormFieldError>
        </div>

        <div class="flex flex-col">
          <FormFieldLabel for="countryStateName">
            {{t('course.claimCertificate.form.personalInformation.fields.state.title')}}
          </FormFieldLabel>

          <InputText
            id="countryStateName"
            v-model="countryStateName"
            :invalid="!!errors['country.stateName']"
            :disabled="loading"
            aria-describedby="countryStateName-help"
            @blur="countryStateNameProps.onBlur"
          />

          <FormFieldError id="countryStateName-help">
            {{errors['country.stateName']}}
          </FormFieldError>
        </div>
      </div>
    </div>

    <!-- QUALITY OF ELM -->
    <div class="flex flex-col gap-6">
      <Divider
        align="left"
        type="solid"
        class="m-0"
        :pt="{
          content: {
            class: 'bg-white'
          }
        }"
      >
        <span class="text-neutral-60 text-14 font-bold uppercase">{{t('course.claimCertificate.form.qualityOfELM.title')}}</span>
      </Divider>

      <!-- Usefulness of material -->
      <div>
        <RatingScale
          v-model="usefulnessProfessional"
          :title="t('course.claimCertificate.form.qualityOfELM.fields.usefulnessOfMaterial.title')"
          :options="UsefulnessOptions.slice(1)"
          :invalid="!!errors['usefulnessOfMaterial.usefulnessProfessional']"
          @update:model-value="usefulnessProfessionalProps.onChange"
        />
        <FormFieldError id="usefulnessProfessional-help">
          {{errors['usefulnessOfMaterial.usefulnessProfessional']}}
        </FormFieldError>
      </div>

      <div
        v-if="usefulnessProfessional === UsefulnessOptions[1]"
        class="flex flex-col gap-2"
      >
        <FormFieldLabel for="notUsefulExplanationProfessional">
          {{t('course.claimCertificate.form.qualityOfELM.fields.usefulnessOfMaterial.whyNotUseful.title')}}
        </FormFieldLabel>

        <Textarea
          id="notUsefulExplanationProfessional"
          v-model="notUsefulExplanationProfessional"
          :placeholder="t('course.claimCertificate.form.textarea.placeholder')"
          :disabled="loading"
          :invalid="!!errors['usefulnessOfMaterial.notUsefulExplanationProfessional']"
          aria-describedby="notUsefulExplanationProfessional-help"
          class="resize-y"
          rows="5"
          @blur="notUsefulExplanationProfessionalProps.onBlur"
        />

        <FormFieldError id="notUsefulExplanationProfessional-help">
          {{errors['usefulnessOfMaterial.notUsefulExplanationProfessional']}}
        </FormFieldError>
      </div>

      <div>
        <RatingScale
          v-model="overallImpressionProgramme"
          :title="t('course.claimCertificate.form.qualityOfELM.fields.overallImpressionProgram.title')"
          :options="OverallImpressionOptions"
          :invalid="!!errors.overallImpressionProgramme"
          @update:model-value="overallImpressionProgrammeProps.onChange"
        />
        <FormFieldError id="overallImpressionProgramme-help">
          {{errors.overallImpressionProgramme}}
        </FormFieldError>
      </div>

      <div>
        <RatingScale
          v-model="overallImpressionOrganization"
          :title="t('course.claimCertificate.form.qualityOfELM.fields.overallImpressionOrganization.title')"
          :options="OverallImpressionOptions"
          :invalid="!!errors.overallImpressionOrganization"
          @update:model-value="overallImpressionOrganizationProps.onChange"
        />
        <FormFieldError id="overallImpressionOrganization-help">
          {{errors.overallImpressionOrganization}}
        </FormFieldError>
      </div>

      <div class="flex flex-col gap-2">
        <FormFieldLabel for="bestAspect">
          {{t('course.claimCertificate.form.qualityOfELM.fields.bestAspectOfMaterial.title')}}
        </FormFieldLabel>

        <Textarea
          id="bestAspect"
          v-model="bestAspect"
          :placeholder="t('course.claimCertificate.form.textarea.placeholder')"
          :disabled="loading"
          :invalid="!!errors.bestAspect"
          aria-describedby="bestAspect-help"
          class="resize-y"
          rows="5"
          @blur="bestAspectProps.onBlur"
        />

        <FormFieldError id="bestAspect-help">
          {{errors.bestAspect}}
        </FormFieldError>
      </div>

      <div class="flex flex-col gap-2">
        <FormFieldLabel for="worstAspect">
          {{t('course.claimCertificate.form.qualityOfELM.fields.worstAspectOfMaterial.title')}}
        </FormFieldLabel>

        <Textarea
          id="worstAspect"
          v-model="worstAspect"
          :placeholder="t('course.claimCertificate.form.textarea.placeholder')"
          :disabled="loading"
          :invalid="!!errors.worstAspect"
          aria-describedby="worstAspect-help"
          class="resize-y"
          rows="5"
          @blur="worstAspectProps.onBlur"
        />

        <FormFieldError id="worstAspect-help">
          {{errors.worstAspect}}
        </FormFieldError>
      </div>
    </div>

    <!-- RELEVANCE OF MATERIAL -->
    <div class="flex flex-col gap-6">
      <Divider
        align="left"
        type="solid"
        class="m-0"
        :pt="{
          content: {
            class: 'bg-white'
          }
        }"
      >
        <span class="text-neutral-60 text-14 font-bold uppercase">
          {{t('course.claimCertificate.form.relevanceOfMaterial.title')}}
        </span>
      </Divider>

      <div>
        <RatingScale
          v-model="achievementOfEducationalGoals"
          :title="t('course.claimCertificate.form.relevanceOfMaterial.fields.achievementOfEducationalGoals.title')"
          :options="EducationalGoalsAndScientificEvidenceOptions"
          :invalid="!!errors.achievementOfEducationalGoals"
          @update:model-value="achievementOfEducationalGoalsProps.onChange"
        />
        <FormFieldError id="achievementOfEducationalGoals-help">
          {{errors.achievementOfEducationalGoals}}
        </FormFieldError>
      </div>

      <div>
        <RatingScale
          v-model="basedOnScientificEvidence"
          :title="t('course.claimCertificate.form.relevanceOfMaterial.fields.basedOnScientificEvidence.title')"
          :options="EducationalGoalsAndScientificEvidenceOptions"
          :invalid="!!errors.basedOnScientificEvidence"
          @update:model-value="basedOnScientificEvidenceProps.onChange"
        />
        <FormFieldError id="basedOnScientificEvidence-help">
          {{errors.basedOnScientificEvidence}}
        </FormFieldError>
      </div>

      <div>
        <RatingScale
          v-model="usefulnessPersonal"
          :title="t('course.claimCertificate.form.relevanceOfMaterial.fields.usefulnessPersonal.title')"
          :options="UsefulnessOptions"
          :invalid="!!errors['usefulnessOfMaterial.usefulnessPersonal']"
          @update:model-value="usefulnessPersonalProps.onChange"
        />
        <FormFieldError id="usefulnessPersonal-help">
          {{errors['usefulnessOfMaterial.usefulnessPersonal']}}
        </FormFieldError>
      </div>

      <div
        v-if="usefulnessPersonal === UsefulnessOptions[1]"
        class="flex flex-col gap-2"
      >
        <FormFieldLabel for="notUsefulExplanationPersonal">
          {{t('course.claimCertificate.form.relevanceOfMaterial.fields.usefulnessPersonal.whyNotUseful.title')}}
        </FormFieldLabel>

        <Textarea
          id="notUsefulExplanationPersonal"
          v-model="notUsefulExplanationPersonal"
          :placeholder="t('course.claimCertificate.form.textarea.placeholder')"
          :disabled="loading"
          :invalid="!!errors['usefulnessOfMaterial.notUsefulExplanationPersonal']"
          aria-describedby="notUsefulExplanationPersonal-help"
          class="resize-y"
          rows="5"
          @blur="notUsefulExplanationPersonalProps.onBlur"
        />

        <FormFieldError id="notUsefulExplanationPersonal-help">
          {{errors['usefulnessOfMaterial.notUsefulExplanationPersonal']}}
        </FormFieldError>
      </div>
    </div>

    <!-- SUITABILITY OF THE FORMATS USED DURING THE MATERIAL -->
    <div class="flex flex-col gap-6">
      <Divider
        align="left"
        type="solid"
        class="m-0"
        :pt="{
          content: {
            class: 'bg-white'
          }
        }"
      >
        <span class="text-neutral-60 text-14 font-bold uppercase">
          {{t('course.claimCertificate.form.suitability.title')}}
        </span>
      </Divider>

      <div>
        <RatingScale
          v-model="learnerEngagementTime"
          :title="t('course.claimCertificate.form.suitability.fields.learnerEngagementTime.title')"
          :options="LearnerEngagementTimeOptions"
          :invalid="!!errors.learnerEngagementTime"
          @update:model-value="learnerEngagementTimeProps.onChange"
        />
        <FormFieldError id="learnerEngagementTime-help">
          {{errors.learnerEngagementTime}}
        </FormFieldError>
      </div>

      <div class="flex flex-col gap-2">
        <FormFieldLabel for="innovativeElements">
          {{t('course.claimCertificate.form.suitability.fields.innovativeElements.title')}}
        </FormFieldLabel>

        <Textarea
          id="innovativeElements"
          v-model="innovativeElements"
          :placeholder="t('course.claimCertificate.form.textarea.placeholder')"
          :disabled="loading"
          :invalid="!!errors.innovativeElements"
          aria-describedby="innovativeElements-help"
          class="resize-y"
          rows="5"
          @blur="innovativeElementsProps.onBlur"
        />

        <FormFieldError id="innovativeElements-help">
          {{errors.innovativeElements}}
        </FormFieldError>
      </div>
    </div>

    <!-- WAYS THE MATERIAL AFFECTS CLINICAL PRACTICE -->
    <div class="flex flex-col gap-6">
      <Divider
        align="left"
        type="solid"
        class="m-0"
        :pt="{
          content: {
            class: 'bg-white'
          }
        }"
      >
        <span class="text-neutral-60 text-14 font-bold uppercase">
          {{t('course.claimCertificate.form.waysMaterialAffectsClinicalPractice.title')}}
        </span>
      </Divider>

      <div>
        <RatingScale
          v-model="informationImplementation"
          :title="t('course.claimCertificate.form.waysMaterialAffectsClinicalPractice.fields.informationImplementation.title')"
          :options="InformationImplementationOptions"
          :invalid="!!errors.informationImplementation"
          @update:model-value="informationImplementationProps.onChange"
        />
        <FormFieldError id="informationImplementation-help">
          {{errors.informationImplementation}}
        </FormFieldError>
      </div>

      <div class="flex flex-col gap-2">
        <FormFieldLabel for="futurePracticeInfluence">
          {{t('course.claimCertificate.form.waysMaterialAffectsClinicalPractice.fields.futurePracticeInfluence.title')}}
        </FormFieldLabel>

        <Textarea
          id="futurePracticeInfluence"
          v-model="futurePracticeInfluence"
          :placeholder="t('course.claimCertificate.form.textarea.placeholder')"
          :disabled="loading"
          :invalid="!!errors.futurePracticeInfluence"
          aria-describedby="futurePracticeInfluence-help"
          class="resize-y"
          rows="5"
          @blur="futurePracticeInfluenceProps.onBlur"
        />

        <FormFieldError id="futurePracticeInfluence-help">
          {{errors.futurePracticeInfluence}}
        </FormFieldError>
      </div>
    </div>

    <!-- COMMERCIAL BIAS -->
    <div class="flex flex-col gap-6">
      <Divider
        align="left"
        type="solid"
        class="m-0"
        :pt="{
          content: {
            class: 'bg-white'
          }
        }"
      >
        <span class="text-neutral-60 text-14 font-bold uppercase">
          {{t('course.claimCertificate.form.commercialBias.title')}}
        </span>
      </Divider>

      <div>
        <RatingScale
          v-model="facultyConflictDeclaration"
          :title="t('course.claimCertificate.form.commercialBias.fields.facultyConflictDeclaration.title')"
          :options="FacultyConflictDeclarationOptions"
          :invalid="!!errors.facultyConflictDeclaration"
          @update:model-value="facultyConflictDeclarationProps.onChange"
        />
        <FormFieldError id="facultyConflictDeclaration-help">
          {{errors.facultyConflictDeclaration}}
        </FormFieldError>
      </div>

      <div class="flex flex-col gap-2">
        <FormFieldLabel for="biasedPresentationExample">
          {{t('course.claimCertificate.form.commercialBias.fields.biasedPresentationExample.title')}}
        </FormFieldLabel>

        <Textarea
          id="biasedPresentationExample"
          v-model="biasedPresentationExample"
          :placeholder="t('course.claimCertificate.form.textarea.placeholder')"
          :disabled="loading"
          :invalid="!!errors.biasedPresentationExample"
          aria-describedby="biasedPresentationExample-help"
          class="resize-y"
          rows="5"
          @blur="biasedPresentationExampleProps.onBlur"
        />

        <FormFieldError id="biasedPresentationExample-help">
          {{errors.biasedPresentationExample}}
        </FormFieldError>
      </div>

      <div>
        <RatingScale
          v-model="informationFreeOfBias"
          :title="t('course.claimCertificate.form.commercialBias.fields.informationFreeOfBias.title')"
          :options="InformationFreeOfBiasOptions"
          :invalid="!!errors.informationFreeOfBias"
          @update:model-value="informationFreeOfBiasProps.onChange"
        />
        <FormFieldError id="informationFreeOfBias-help">
          {{errors.informationFreeOfBias}}
        </FormFieldError>
      </div>
    </div>
    <Divider class="m-0" />

    <!-- AGREEMENT -->
    <div class="flex flex-col gap-3">
      <div>
        <label class="flex gap-2 text-14 font-bold">
          <Checkbox
            v-model="userAgreedToDataSharing"
            binary
            :disabled="loading"
            @update:model-value="userAgreedToDataSharingProps.onBlur"
          />
          <span>
            {{t('course.claimCertificate.form.agreement.disclaimer')}}
          </span>
        </label>
        <FormFieldError id="userAgreedToDataSharing-help">
          {{errors.userAgreedToDataSharing}}
        </FormFieldError>
      </div>

      <div class="text-neutral-70 text-14 leading-150">
        UEMS <sub>aisbl</sub> – Union Européenne des Médecins Spécialistes <br>
        VAT n° BE 0469.067.848 RPM Bruxelles-Brussels <br>
        EU Transparency Register ID 219038730914-92
      </div>
    </div>

    <Divider class="m-0" />

    <!-- COURSE RATING -->
    <div class="flex flex-col gap-4">
      <div class="flex flex-col items-center gap-2">
        <div class="text-16">
          {{t('course.claimCertificate.form.rating.title')}}
        </div>
        <StarRating
          v-model="courseRating"
          :disabled="loading"
          @update:model-value="courseRatingProps.onBlur"
        />
        <FormFieldError id="courseRating-help">
          {{errors.courseRating}}
        </FormFieldError>
      </div>

      <div class="flex justify-center">
        <Button
          :loading="loading"
          class="font-bold !text-22 flex gap-2 px-12 bg-blue-30 text-neutral-90 border-none rounded-full"
          type="submit"
          @click="onSubmit"
        >
          <FontAwesomeIcon
            v-if="loading"
            icon="fa-circle-notch"
            class="animate-spin"
          />
          <FontAwesomeIcon
            v-else
            icon="fa-file-certificate"
          />
          <span>
            {{t('course.claimCertificate.form.submit.label')}}
          </span>
        </Button>
      </div>
      <div class="flex justify-center mb-12">
        <FormFieldError
          v-if="Object.keys(errors).length > 0"
          id="form-error"
        >
          {{t('course.claimCertificate.form.submit.validationErrorMessage')}}
        </FormFieldError>
      </div>
    </div>
  </form>
</template>